// built-from: /vision-capture-screen v2 impl:vision-capture-screen role:implementation
// built-from: /contextual-facilitator-companion v2 impl:contextual-facilitator-companion role:implementation
import VisionSurface from '../components/vision/VisionSurface';
import { attachFacilitator } from './ContextualFacilitatorCompanion';
import { ActivityShell } from './ActivityShell';
import {
  ActivityAttachment,
  ActivityConversationPanel,
  existingSessionBinding,
  useActivityController,
  type ActivityFacilitationBinding,
} from './ActivityConversation';
import { ADD_ACTIVITY_TO_PLAN_CAPABILITY_ID } from './activityPlanCapability';

const ALLOWED_VISION_COMMAND_IDS = Object.freeze([
  ADD_ACTIVITY_TO_PLAN_CAPABILITY_ID,
  'vision.add_goal', 'vision.add_obstacle', 'vision.add_document_facts',
  'vision.confirm_fact', 'vision.edit_fact', 'vision.dispute_fact',
  'vision.reconcile_fact', 'vision.reject_fact', 'vision.raise_draft',
  'vision.designate_crux', 'vision.propose_tier', 'vision.confirm_tier',
  'vision.add_challenge', 'vision.add_evidence', 'vision.propose_crux_candidate',
  'vision.reject_crux_candidate', 'vision.set_guiding_policy',
  'vision.set_participant', 'vision.set_north_star', 'vision.add_driver',
  'vision.add_objective', 'vision.add_benefit', 'vision.set_benefit_detail',
  'vision.close_session',
]);

// 100: Vision renders through the ONE activity shell. Its private
// companionOpen state, hand-rolled header and "Open facilitator" button are
// gone; the conversation is there from the moment the screen opens.
function VisionScreenContent({ facilitation }: { facilitation: ActivityFacilitationBinding }) {
  const controller = useActivityController();
  return (
    <ActivityShell
      title="Vision articulation"
      context="Facilitated working session"
      saving={controller.pendingCount > 0}
      sealed={controller.state.sealed}
      conversation={{
        render: ({ close }) => <ActivityConversationPanel facilitation={facilitation} onClose={close} />,
        opening: facilitation.opening,
        error: facilitation.error || null,
        onRetry: facilitation.open,
      }}
    >
      {controller.rejection ? <div role="alert" className="fshell-alert">{controller.rejection}</div> : null}
      <VisionSurface
        state={controller.state}
        busy={controller.pendingCount > 0}
        onEmitEdit={(edit) => controller.execute(edit)}
        onPublishFocus={controller.publishFocus}
      />
    </ActivityShell>
  );
}

export default function VisionFacilitationScreen({ facilitation, sessionId }: {
  facilitation?: ActivityFacilitationBinding;
  // The legacy vision route hands the session id straight in; its conversation
  // is already open, so there is nothing to defer.
  sessionId?: string;
}) {
  const binding = facilitation || existingSessionBinding(sessionId || '');
  const attachment = attachFacilitator({
    screenId: 'vision-surface',
    profileId: 'vision',
    resolveSession: () => binding.sessionId || null,
    getContext: () => ({ revision: 0, focus: null, values: {} }),
    allowedCapabilityIds: ALLOWED_VISION_COMMAND_IDS,
    actions: { completion: { label: 'Complete and close session' } },
  });
  return (
    <ActivityAttachment facilitation={binding} attachment={attachment}>
      <VisionScreenContent facilitation={binding} />
    </ActivityAttachment>
  );
}
